import type { ModelPricing, AgentRecommendation } from "@fathom/core";
import type { IntelligenceSuggestion } from "./types.js";

export interface ModelChange {
  id: string;
  name: string;
  status: "added" | "updated" | "unchanged";
  fields?: Array<{ field: string; old: number | string; new: number | string }>;
}

export interface RecommendationChange {
  category: string;
  status: "added" | "updated" | "unchanged";
  oldModel?: string;
  newModel?: string;
  reason?: string;
}

export interface MergeResult {
  models: {
    merged: ModelPricing[];
    changes: ModelChange[];
    added: number;
    updated: number;
    unchanged: number;
  };
  recommendations: {
    merged: Record<string, AgentRecommendation>;
    changes: RecommendationChange[];
    added: number;
    updated: number;
    unchanged: number;
  };
  suggestions: IntelligenceSuggestion[];
}

export function mergePricingData(
  localModels: ModelPricing[],
  remoteModels: ModelPricing[],
  localRecs: Record<string, AgentRecommendation>,
  remoteRecs: Record<string, AgentRecommendation>,
  suggestions: IntelligenceSuggestion[] = [],
): MergeResult {
  // Merge models: remote wins, local-only models preserved
  const mergedModels: ModelPricing[] = [];
  const modelChanges: ModelChange[] = [];
  const seenIds = new Set<string>();

  for (const remote of remoteModels) {
    seenIds.add(remote.id);
    const local = localModels.find((m) => m.id === remote.id);

    if (!local) {
      mergedModels.push(remote);
      modelChanges.push({ id: remote.id, name: remote.name, status: "added" });
      continue;
    }

    const fields: ModelChange["fields"] = [];
    const pricingKeys: (keyof ModelPricing)[] = [
      "input_per_mtok",
      "output_per_mtok",
      "cache_read_per_mtok",
      "batch_discount",
    ];

    for (const key of pricingKeys) {
      if (local[key] !== remote[key]) {
        fields.push({ field: key, old: local[key], new: remote[key] });
      }
    }

    if (local.tier !== remote.tier) {
      fields.push({ field: "tier", old: local.tier, new: remote.tier });
    }

    mergedModels.push(remote);
    modelChanges.push({
      id: remote.id,
      name: remote.name,
      status: fields.length > 0 ? "updated" : "unchanged",
      fields: fields.length > 0 ? fields : undefined,
    });
  }

  // Preserve local-only models
  for (const local of localModels) {
    if (!seenIds.has(local.id)) {
      mergedModels.push(local);
      modelChanges.push({ id: local.id, name: local.name, status: "unchanged" });
    }
  }

  // Merge recommendations: remote wins, local-only preserved
  const mergedRecs: Record<string, AgentRecommendation> = { ...localRecs };
  const recChanges: RecommendationChange[] = [];

  for (const [cat, remoteRec] of Object.entries(remoteRecs)) {
    const localRec = localRecs[cat];

    if (!localRec) {
      mergedRecs[cat] = remoteRec;
      recChanges.push({
        category: cat,
        status: "added",
        newModel: remoteRec.model,
        reason: remoteRec.reason,
      });
    } else if (localRec.model !== remoteRec.model || localRec.reason !== remoteRec.reason) {
      mergedRecs[cat] = remoteRec;
      recChanges.push({
        category: cat,
        status: "updated",
        oldModel: localRec.model,
        newModel: remoteRec.model,
        reason: remoteRec.reason,
      });
    } else {
      recChanges.push({ category: cat, status: "unchanged" });
    }
  }

  return {
    models: {
      merged: mergedModels,
      changes: modelChanges,
      added: modelChanges.filter((c) => c.status === "added").length,
      updated: modelChanges.filter((c) => c.status === "updated").length,
      unchanged: modelChanges.filter((c) => c.status === "unchanged").length,
    },
    recommendations: {
      merged: mergedRecs,
      changes: recChanges,
      added: recChanges.filter((c) => c.status === "added").length,
      updated: recChanges.filter((c) => c.status === "updated").length,
      unchanged: recChanges.filter((c) => c.status === "unchanged").length,
    },
    suggestions,
  };
}

export function generateModelsYaml(models: ModelPricing[]): string {
  const date = new Date().toISOString().split("T")[0];
  let yaml = `# Model pricing registry — updated via \`fathom research\`\n`;
  yaml += `# Last verified: ${date}\n\n`;
  yaml += `models:\n`;

  for (const m of models) {
    yaml += `  - id: ${m.id}\n`;
    yaml += `    provider: ${m.provider}\n`;
    yaml += `    name: ${m.name}\n`;
    yaml += `    input_per_mtok: ${m.input_per_mtok.toFixed(2)}\n`;
    yaml += `    output_per_mtok: ${m.output_per_mtok.toFixed(2)}\n`;
    yaml += `    cache_read_per_mtok: ${m.cache_read_per_mtok.toFixed(2)}\n`;
    yaml += `    batch_discount: ${m.batch_discount.toFixed(2)}\n`;
    yaml += `    tier: ${m.tier}\n\n`;
  }

  return yaml;
}

export function generateAgentsYaml(
  recommendations: Record<string, AgentRecommendation>,
): string {
  const date = new Date().toISOString().split("T")[0];
  let yaml = `# Agent/model recommendation mappings\n`;
  yaml += `# Maps task categories to recommended models\n`;
  yaml += `# Last updated: ${date}\n\n`;
  yaml += `recommendations:\n`;

  for (const [category, rec] of Object.entries(recommendations)) {
    yaml += `  ${category}:\n`;
    yaml += `    model: ${rec.model}\n`;
    yaml += `    reason: ${rec.reason}\n\n`;
  }

  return yaml;
}
